import { useEffect, useMemo, useState } from "react";
import { TrendingUp, ArrowDown, FlaskConical, Check, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ROI_BASE, readRoiDelta } from "@/lib/roi";
import { Card, Funnel, TONE, fmt, tooltipStyle } from "@/components/impact/ImpactWidgets";
import AttributionSection from "@/components/impact/AttributionSection";
import RoiCalculator from "@/components/impact/RoiCalculator";

export default function Impact() {
  const [delta, setDelta] = useState(readRoiDelta);
  const [openStage, setOpenStage] = useState(null);

  useEffect(() => {
    const h = () => setDelta(readRoiDelta());
    window.addEventListener("onet-roi-delta", h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener("onet-roi-delta", h); window.removeEventListener("storage", h); };
  }, []);

  const B = ROI_BASE;
  const d = delta;
  const live = d.assistedFtdValue > 0 || d.reengaged > 0 || d.escalations > 0 || d.governance > 0 || d.organicValue > 0;

  const dist = useMemo(() => {
    const automated = B.distribution.automated;
    const human = B.distribution.human + d.escalations;
    const suppressed = B.distribution.suppressed + d.governance;
    const total = automated + human + suppressed;
    return [
      { label: "Automated eligible actions", value: automated, pct: (automated / total * 100).toFixed(1), tone: "emerald" },
      { label: "Human escalations", value: human, pct: (human / total * 100).toFixed(1), tone: "sky", changed: d.escalations > 0 },
      { label: "Suppressed / no action", value: suppressed, pct: (suppressed / total * 100).toFixed(1), tone: "amber", changed: d.governance > 0 },
    ];
  }, [B, d]);

  const totalProgressions = B.summary.assistedProgressions + d.assistedFtdCount + d.reengaged;
  const assistedFtds = B.funnels.ftd.steps[5].value + d.assistedFtdCount;

  const summaryItems = [
    { label: "Lifecycle opportunities detected", value: B.summary.detected },
    { label: "Eligible interventions", value: B.summary.eligible },
    { label: "Automated actions", value: B.summary.automated },
    { label: "Human escalations", value: B.summary.human + d.escalations, changed: d.escalations > 0 },
    { label: "Actions prevented by governance", value: B.summary.governancePrevented + d.governance, changed: d.governance > 0 },
    { label: "Assisted client progressions", value: totalProgressions, changed: d.assistedFtdCount > 0 || d.reengaged > 0 },
    { label: "Assisted FTD value", value: fmt(B.summary.assistedFtdValue + d.assistedFtdValue), changed: d.assistedFtdValue > 0 },
    { label: "Assisted retention account value", value: fmt(B.summary.assistedRetentionValue) },
    { label: "Avg time to eligible intervention", value: `${B.summary.avgInterventionSec} sec` },
  ];

  const primaryCards = [
    { label: "Assisted Revenue", sub: "Eligible successful outcomes with an O.N.E.Tech intervention inside the attribution window", value: fmt(B.primary.assistedRevenue + d.assistedFtdValue), changed: d.assistedFtdValue > 0, testId: "primary-assisted-revenue" },
    { label: "Recovered Opportunities", sub: "Stalled or at-risk journeys that progressed after intervention", value: B.primary.recoveredOpportunities + d.assistedFtdCount + d.reengaged, changed: d.assistedFtdCount > 0 || d.reengaged > 0, testId: "primary-recovered" },
    { label: "Estimated Manual Workload Redirected", sub: "Routine handling automated so teams focus on high-value and sensitive cases — not a staff-reduction metric", value: `${B.primary.hoursRedirected} hrs`, testId: "primary-hours" },
    { label: "Time to Intervention", sub: `Avg, qualifying event → action · Illustrative manual process: ${B.primary.manualBaselineMin} min (not broker historical data)`, value: `${B.primary.avgInterventionSec} sec`, testId: "primary-tti" },
  ];

  const governanceRows = [
    ["Actions prevented", B.governance.prevented + d.governance, d.governance > 0],
    ["Sensitive cases routed to humans", B.governance.sensitiveRouted + d.escalations, d.escalations > 0],
    ["Consent blocks", B.governance.consentBlocks, false],
    ["Communication frequency blocks", B.governance.frequencyBlocks, false],
    ["Compliance-restricted actions", B.governance.complianceBlocks, false],
    ["Suppression-rule blocks", B.governance.suppressionBlocks, false],
    ["SLA escalations", B.governance.slaEscalations, false],
  ];

  const humanRows = [
    ["Open high-priority cases", B.humanImpact.open + d.escalations, d.escalations > 0],
    ["Account value represented", fmt(B.humanImpact.value), false],
    ["SLA within target", `${B.humanImpact.slaWithin}%`, false],
    ["SLA breaches", B.humanImpact.breaches, false],
    ["Resolved cases", B.humanImpact.resolved, false],
    ["Human-assisted outcomes", B.humanImpact.humanAssistedOutcomes, false],
  ];

  return (
    <div className="space-y-6" data-testid="impact-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
            <TrendingUp className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Operational Impact
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Measured outcomes from detected lifecycle opportunities and interventions.</p>
        </div>
        <div className="flex items-center gap-2">
          {live && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold border border-red-500/40 bg-red-500/10 text-red-400" data-testid="roi-live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> LIVE SESSION DELTAS APPLIED
            </span>
          )}
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-400" data-testid="simulated-label">
            <FlaskConical className="w-3 h-3" strokeWidth={1.5} /> SIMULATED DEMO DATA — ILLUSTRATIVE OPERATIONAL IMPACT
          </span>
        </div>
      </div>

      <Card title="Executive Summary" sub="All values derive from the current simulated dataset" testId="executive-summary">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-3">
          {summaryItems.map((s, i) => (
            <div key={s.label} className={`rounded-sm p-3 border ${s.changed ? "border-emerald-500/50 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/50"}`} data-testid={`summary-${i}`}>
              <div className={`text-xl font-bold font-mono ${s.changed ? "text-emerald-400" : "text-zinc-100"}`}>{s.value}</div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-1 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {primaryCards.map((k) => (
          <div key={k.label} className={`bg-zinc-900 border rounded-md p-5 transition-colors duration-500 ${k.changed ? "border-emerald-500/50 shadow-[0_0_15px_rgba(16,185,129,0.12)]" : "border-zinc-800"}`} data-testid={k.testId}>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{k.label}</div>
            <div className={`text-3xl font-bold font-mono tracking-tight mt-2 ${k.changed ? "text-emerald-400" : "text-zinc-100"}`}>{k.value}</div>
            <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">{k.sub}</p>
          </div>
        ))}
      </div>

      <Card title="Value at Risk vs Value Recovered" sub="Deposit-value basis · categories reconcile to the simulated total · retention shown separately as account value" testId="reconciliation">
        <div className="flex flex-wrap items-center gap-2.5 mb-4">
          <span className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">Value at risk identified</span>
          <span className="font-mono text-2xl font-bold text-red-400" data-testid="value-at-risk-total">{fmt(B.valueAtRiskTotal)}</span>
          <span className="text-[10px] font-mono text-zinc-600">POTENTIAL VALUE AT RISK — not revenue lost</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {B.reconciliation.map((r) => {
            const v = r.key === "progress"
              ? r.value - d.assistedFtdValue - d.organicValue
              : r.value + (r.deltaKey ? d[r.deltaKey] || 0 : 0);
            const changed = (r.deltaKey && (d[r.deltaKey] || 0) > 0) || (r.key === "progress" && (d.assistedFtdValue > 0 || d.organicValue > 0));
            return (
              <div key={r.key} className={`rounded-sm border p-3.5 ${changed ? "border-emerald-500/50 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/50"}`} data-testid={`recon-${r.key}`}>
                <div className={`text-lg font-bold font-mono ${changed ? "text-emerald-400" : "text-zinc-100"}`}>{fmt(v)}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{r.label}</div>
                {r.key === "organic" && d.organicValue > 0 && (
                  <div className="text-[9px] font-mono text-zinc-600 mt-1">incl. £3,000 GBP (illustrative conversion)</div>
                )}
              </div>
            );
          })}
        </div>
        {(d.assistedFtdValue > 0 || d.organicValue > 0) && (
          <div className="text-[10px] font-mono text-zinc-600 mt-2">Live-session amounts transfer between categories — the €226,000 total is unchanged.</div>
        )}
        <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="leakage-summary">
          <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-3">Current simulated operational leakage</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {B.leakageSummary.map((a) => {
              const v = a.label === "Still Open"
                ? a.value - d.assistedFtdValue - d.organicValue
                : a.value + (a.deltaKey ? d[a.deltaKey] || 0 : 0);
              return (
                <div key={a.label} className="flex items-center justify-between sm:flex-col sm:items-start gap-1 border border-zinc-800 bg-zinc-950/50 rounded-sm px-3 py-2.5">
                  <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{a.label}</span>
                  <span className={`font-mono text-sm font-semibold ${TONE[a.tone].split(" ")[0]}`}>{fmt(v)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Revenue Leakage Identified" sub="Where opportunities are currently being lost or becoming vulnerable" testId="leakage-areas">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Lifecycle area</th>
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Opportunities</th>
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Potential value at risk</th>
              </tr>
            </thead>
            <tbody>
              {B.leakageAreas.map((a) => (
                <tr key={a.area} className="border-b border-zinc-800/60">
                  <td className="py-2.5 text-xs text-zinc-300">{a.area}</td>
                  <td className="py-2.5 text-xs font-mono text-zinc-200 text-right">{a.opportunities}</td>
                  <td className="py-2.5 text-xs font-mono text-red-400 text-right">{fmt(a.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card title="Where Value Is Currently at Risk — by Stage" sub="Click a stage to inspect the underlying simulated clients" testId="leakage-stages">
          <div className="space-y-2">
            {B.leakageStages.map((s, i) => (
              <div key={s.stage}>
                <button
                  data-testid={`stage-toggle-${i}`}
                  onClick={() => setOpenStage(openStage === i ? null : i)}
                  className="w-full flex items-center justify-between rounded-sm border border-zinc-800 bg-zinc-950/50 hover:border-zinc-600 px-3.5 py-2.5 transition-colors duration-200"
                >
                  <span className="flex items-center gap-2 text-xs text-zinc-300">
                    {openStage === i ? <ChevronUp className="w-3.5 h-3.5 text-emerald-400" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                    {s.stage}
                  </span>
                  <span className="font-mono text-xs text-red-400">{fmt(s.value)}</span>
                </button>
                {openStage === i && (
                  <div className="mt-1.5 ml-4 border-l-2 border-emerald-500/30 pl-3 space-y-1.5 fade-slide-in" data-testid={`stage-clients-${i}`}>
                    {s.clients.map((c) => (
                      <div key={c.ref} className="flex items-center justify-between text-[11px]">
                        <span className="text-zinc-300">{c.name} <span className="font-mono text-zinc-600">{c.ref}</span> — <span className="text-zinc-500">{c.issue}</span></span>
                        <span className="font-mono text-zinc-400">{fmt(c.value)}</span>
                      </div>
                    ))}
                    <div className="text-[10px] text-zinc-600 italic">Illustrative sample of contributing simulated clients.</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Funnel title={B.funnels.ftd.title} steps={B.funnels.ftd.steps} note={B.funnels.ftd.note} delta={d} testId="funnel-ftd" />
        <Funnel title={B.funnels.kyc.title} steps={B.funnels.kyc.steps} note={B.funnels.kyc.note} delta={d} testId="funnel-kyc" />
        <Funnel title={B.funnels.retention.title} steps={B.funnels.retention.steps} note={B.funnels.retention.note} delta={d} testId="funnel-retention" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Time to Intervention" sub="Qualifying event → O.N.E.Tech action (simulated)" testId="response-times">
          <div className="grid grid-cols-2 gap-3">
            {B.responseTimes.onetech.map((r) => (
              <div key={r.label} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-3">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{r.label}</div>
                <div className="font-mono text-lg font-bold text-emerald-400 mt-1">{r.sec} sec</div>
              </div>
            ))}
          </div>
          <div className="mt-3 border border-zinc-800 rounded-sm p-3 bg-zinc-950/30">
            <div className="text-[10px] font-mono text-amber-500/90 uppercase tracking-wider mb-2">Illustrative manual baseline — not broker historical data</div>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {B.responseTimes.manual.map((m) => (
                <span key={m.label} className="text-[11px] text-zinc-400">{m.label}: <span className="font-mono text-zinc-200">{m.label2}</span></span>
              ))}
            </div>
          </div>
        </Card>

        <Card title="Intervention Performance" sub="Rates computed directly from the simulated dataset" testId="intervention-performance">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Workflow</th>
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Actions</th>
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Progressions</th>
                <th className="py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Assisted rate</th>
              </tr>
            </thead>
            <tbody>
              {B.performance.map((p) => {
                const prog = p.progressions + (p.deltaProgressions ? d[p.deltaProgressions] || 0 : 0);
                const rate = ((prog / p.actions) * 100).toFixed(1);
                return (
                  <tr key={p.workflow} className="border-b border-zinc-800/60">
                    <td className="py-2.5 text-xs text-zinc-300">{p.workflow}</td>
                    <td className="py-2.5 text-xs font-mono text-zinc-200 text-right">{p.actions}</td>
                    <td className="py-2.5 text-xs font-mono text-zinc-200 text-right">{prog}</td>
                    <td className="py-2.5 text-xs font-mono text-emerald-400 text-right">{rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Operational Distribution" sub="Automate what is safe, route what requires people, block what should not happen" testId="operational-distribution">
          <div className="space-y-3">
            {dist.map((x) => (
              <div key={x.label}>
                <div className="flex justify-between text-[11px] mb-1">
                  <span className="text-zinc-400">{x.label}</span>
                  <span className={`font-mono font-semibold ${x.changed ? "text-emerald-400" : "text-zinc-200"}`}>{x.value} · {x.pct}%</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${x.tone === "emerald" ? "bg-emerald-500" : x.tone === "sky" ? "bg-sky-400" : "bg-amber-400"}`} style={{ width: `${x.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Human Escalation Impact" sub="O.N.E.Tech improves human coordination, not just automation" testId="human-impact">
          <div className="grid grid-cols-2 gap-2.5">
            {humanRows.map(([k, v, changed]) => (
              <div key={k} className={`border rounded-sm p-3 ${changed ? "border-emerald-500/50 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950/50"}`}>
                <div className={`text-lg font-bold font-mono ${changed ? "text-emerald-400" : "text-zinc-100"}`}>{v}</div>
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-1">{k}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Governance & Control" sub="Value created by preventing inappropriate actions" testId="governance-value">
          <div className="space-y-2">
            {governanceRows.map(([k, v, changed]) => (
              <div key={k} className="flex items-center justify-between text-xs border-b border-zinc-800/60 pb-1.5">
                <span className="text-zinc-400 flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-emerald-500" strokeWidth={1.5} />{k}</span>
                <span className={`font-mono font-semibold ${changed ? "text-emerald-400" : "text-zinc-200"}`}>{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <AttributionSection B={B} d={d} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Outcome by Channel" sub="Simulated performance only — no universal channel conclusion" testId="outcome-by-channel">
          <div className="space-y-2.5">
            {B.channels.map((c) => (
              <div key={c.channel} className="flex items-center gap-3">
                <span className="text-xs text-zinc-300 w-24">{c.channel}</span>
                <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(c.progressions / c.interventions) * 100}%` }} />
                </div>
                <span className="text-[11px] font-mono text-zinc-400">{c.interventions} interventions · {c.progressions} progressions</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Assisted Outcomes by Language" sub="Multilingual communication as measurable operational infrastructure" testId="outcome-by-language">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
            {B.languages.map((l) => (
              <div key={l.language} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-3 text-center">
                <div className="text-xl font-bold font-mono text-emerald-400">{l.outcomes}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{l.language}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Illustrative Existing Workflow vs With O.N.E.Tech" sub="Process efficiency and visibility comparison — the left side is not a proven historical baseline" testId="before-after">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/50">
            <div className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase mb-3">Illustrative existing workflow</div>
            {B.beforeAfter.before.map((s, i) => (
              <div key={s}>
                <div className="text-xs text-zinc-400 py-1">{s}</div>
                {i < B.beforeAfter.before.length - 1 && <ArrowDown className="w-3 h-3 text-zinc-700 my-0.5" strokeWidth={1.5} />}
              </div>
            ))}
          </div>
          <div className="border border-emerald-500/30 rounded-sm p-4 bg-emerald-500/5">
            <div className="text-[10px] font-mono tracking-widest text-emerald-400 uppercase mb-3">With O.N.E.Tech</div>
            {B.beforeAfter.after.map((s, i) => (
              <div key={s}>
                <div className="text-xs text-zinc-200 py-1 flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" strokeWidth={1.5} />{s}</div>
                {i < B.beforeAfter.after.length - 1 && <ArrowDown className="w-3 h-3 text-emerald-700 my-0.5" strokeWidth={1.5} />}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card title="Simulated Trends — Weekly" sub="Believable operational variation, 12-week window" testId="trends">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {B.trends.series.map((s) => (
            <div key={s.key} data-testid={`trend-${s.key}`}>
              <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">{s.label}</div>
              <ResponsiveContainer width="100%" height={90}>
                <BarChart data={s.data.map((v, i) => ({ w: B.trends.weeks[i], v }))} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                  <XAxis dataKey="w" tick={{ fill: "#52525b", fontSize: 8 }} interval={3} />
                  <YAxis tick={{ fill: "#52525b", fontSize: 8 }} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a" }} />
                  <Bar dataKey="v" fill="#10b981" radius={[1, 1, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Open Opportunity Value" sub="Active revenue opportunities still requiring attention" testId="open-opportunity">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-mono text-2xl font-bold text-sky-400">{fmt(B.openOpportunity.reduce((a, b) => a + b.value, 0))}</span>
            <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">still open</span>
          </div>
          <div className="space-y-2">
            {B.openOpportunity.map((o) => (
              <div key={o.area} className="flex items-center justify-between text-xs border-b border-zinc-800/60 pb-1.5">
                <span className="text-zinc-400">{o.area}</span>
                <span className="font-mono text-zinc-200">{fmt(o.value)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Cost per Recovered Outcome" sub="Simulated operational efficiency — populated with production data later" testId="cost-per-outcome">
          <div className="space-y-3 text-xs">
            <div className="flex justify-between border-b border-zinc-800/60 pb-1.5"><span className="text-zinc-400">Monthly platform allocation (simulated)</span><span className="font-mono text-zinc-200">{fmt(B.cost.platformMonthly)}</span></div>
            <div className="flex justify-between border-b border-zinc-800/60 pb-1.5"><span className="text-zinc-400">Assisted FTDs</span><span className="font-mono text-zinc-200">{assistedFtds}</span></div>
            <div className="flex justify-between border-b border-zinc-800/60 pb-1.5"><span className="text-zinc-400">Cost per assisted FTD</span><span className="font-mono text-emerald-400">{fmt(B.cost.platformMonthly / assistedFtds)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Cost per assisted lifecycle progression</span><span className="font-mono text-emerald-400">{fmt(B.cost.platformMonthly / totalProgressions)}</span></div>
          </div>
        </Card>
      </div>

      <RoiCalculator />

      <div className="border border-amber-500/20 bg-amber-500/5 rounded-sm px-4 py-3 text-[11px] font-mono text-amber-500/90" data-testid="impact-disclaimer">
        All figures on this page are simulated for demonstration and internally reconcile to the current demo dataset. They demonstrate how production ROI would be measured on real broker data — no uplift, recovery or ROI is guaranteed. Client deposits are never broker revenue.
      </div>
    </div>
  );
}
